"use client"

import clsx from "clsx"
import { Loader2 } from "lucide-react"
import { memo, useCallback, useState, type MouseEvent, type MouseEventHandler, type ReactNode } from "react"
import { useDataAttributes } from "../../hooks"
import { BaseInteractiveProps } from "../../utils"

/**
 * 커스텀 버튼 컴포넌트
 *
 * @description
 * 다양한 스타일과 크기를 지원하는 버튼 컴포넌트입니다.
 * 비동기 작업 시 자동으로 로딩 상태를 관리합니다.
 *
 * @example
 * ```tsx
 * // 기본 사용
 * <TButton color="primary" size="medium" onClick={handleClick}>
 *   클릭
 * </TButton>
 *
 * // 비동기 작업
 * <TButton onClick={async () => await saveData()}>
 *   저장
 * </TButton>
 *
 * // 로딩 상태
 * <TButton loading={isLoading}>
 *   처리중...
 * </TButton>
 * ```
 */
interface TButtonProps extends BaseInteractiveProps {
	/** 버튼 DOM 요소 참조. focus(), blur() 등 직접 제어 시 사용 */
	ref?: React.Ref<HTMLButtonElement>
	/** 버튼 타입 (HTML button type 속성) @default "button" */
	type?: "submit" | "reset" | "button"
	/**
	 * 버튼 색상 테마
	 * @default "primary"
	 * @ai-style "primary" → 녹색 배경(bg-p400, #00db63), 어두운 텍스트 — CTA/주요 액션용
	 * @ai-style "secondary" → 연초록 배경(bg-p100, #c4f6d6), 진초록 텍스트(text-p700) — 보조 액션
	 * @ai-style "tertiary" → 밝은 회색 배경(bg-g100), 어두운 텍스트 — 덜 중요한 액션/취소
	 * @ai-style "line" → 투명 배경, 회색 테두리(1.5px) — 윤곽선 스타일
	 * @ai-style "ghost" → 투명 배경, hover 시 연회색 — 최소 시각적 존재감
	 * @ai-style "black" → 어두운 배경(bg-g700), 밝은 텍스트 — 강조
	 * @ai-style "blurry" → 반투명(bg-g0/20) + backdrop-blur — 이미지/오버레이 위 버튼
	 */
	color?: "primary" | "secondary" | "tertiary" | "line" | "ghost" | "black" | "blurry"
	/**
	 * 버튼 크기
	 * @default "medium"
	 * @ai-style "xsmall" → 높이 24px, px-2, text-xs
	 * @ai-style "small" → 높이 32px, px-2, text-sm
	 * @ai-style "medium" → 높이 40px, px-4, text-base
	 * @ai-style "large" → 높이 48px, px-5, text-lg, 더 둥근 모서리(rounded-[10px])
	 */
	size?: "xsmall" | "small" | "medium" | "large"
	/** 선택된 상태 표시 여부 (더 진한 배경색으로 강조) @default false */
	selected?: boolean
	/** 로딩 상태 (true일 경우 스피너 표시 및 클릭 비활성화) @default false */
	loading?: boolean
	/** 버튼 내용 */
	children: ReactNode
	/** 클릭 이벤트 핸들러 (Promise 반환 시 자동으로 로딩 상태 관리) */
	onClick?: (event: MouseEvent<HTMLButtonElement>) => Promise<void> | void
}

function TButtonInner(props: TButtonProps) {
	const {
		// HTML 속성
		id,
		className,
		type = "button",
		// 스타일
		color = "primary",
		size = "medium",
		// 상태
		selected,
		disabled,
		loading,
		// 데이터
		dataAttributes,
		children,
		// 핸들러
		onClick,
		// React 19 ref prop
		ref,
		...restProps
	} = props

	const [processing, setProcessing] = useState<boolean>(false)
	const isLock = processing || loading
	const dataProps = useDataAttributes(dataAttributes)

	const handleClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
		(event) => {
			if (disabled || loading || processing) {
				event.preventDefault()
				return
			}
			const result = onClick?.(event)
			if (result instanceof Promise) {
				setProcessing(true)
				result.finally(() => {
					setProcessing(false)
				})
			}
		},
		[disabled, loading, processing, onClick]
	)

	return (
		<button
			{...restProps}
			ref={ref}
			id={id}
			className={clsx("TButton", color, size, className)}
			type={type}
			disabled={disabled}
			data-loading={isLock}
			data-selected={selected}
			{...dataProps}
			onClick={handleClick}
		>
			{isLock ? (
				<span className="loader">
					<Loader2 aria-hidden="true" />
				</span>
			) : null}
			<span className={clsx("label", { invisible: isLock })}>{children}</span>
		</button>
	)
}

TButtonInner.displayName = "TButton"

export const TButton = memo(TButtonInner)
